import React from 'react';
import { map, get, isEmpty, isPlainObject, omit } from 'lodash';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  CircularProgress,
} from '@mui/material';
import cx from 'classnames';
import { ReactComponent as ArrowDropDown } from 'src/new/components/icons/chevron_down.svg';
import { ReactComponent as ArrowRight } from 'src/new/components/icons/chevron_right.svg';
import LoadableMonacoEditor from 'js/components/elements/editors/monaco/LoadableMonacoEditor';
import JSONUtils from 'js/lib/JSONUtils';
import styles from './SamplesPayloadTab.module.scss';

const SamplesPayloadTab = ({
  handleRenderHeader,
  description,
  error,
  isLoadingProbeSample,
  previewPayload,
  batchMode,
  groupingAlgo,
  editorLanguage,
  batchedCode,
  sinkConfig,
  contentType,
  panelHeading,
  heading,
  refreshPayload,
}) => {
  const [activePanelKey, setActivePanelKey] = React.useState(null);

  const selectPanel = (panelKey) => {
    setActivePanelKey((prevState) => (panelKey !== prevState ? panelKey : null));
  };

  const renderHeader = () => {
    return (
      <div className={styles.header_container}>
        {(error || isEmpty(previewPayload)) && (
          <Button color="info" variant="outlined" onClick={refreshPayload}>
            {isLoadingProbeSample ? <CircularProgress color="info" size={15} /> : 'Refresh'}
          </Button>
        )}
      </div>
    );
  };

  let content;

  if (error) {
    content = (
      <div className={styles.error_container}>
        <span
          style={{
            marginRight: 'var(--spacing-md)',
            fontSize: 'var(--font-size-sm)',
            color: 'red',
          }}
        >
          {error}
        </span>
      </div>
    );
  } else if (isLoadingProbeSample) {
    content = 'Loading...';
  } else if (isEmpty(previewPayload)) {
    content = 'No samples found.';
  } else {
    content = (
      <div id="request-payloads" className={styles.accordion_group_container}>
        {batchMode && groupingAlgo && groupingAlgo.value !== 'code' ? (
          <Accordion square className="accordion-item" expanded style={{ position: 'initial' }}>
            <AccordionSummary className="accordion-title">
              <div className={styles.accordion_title_content}>
                <h2>
                  <ArrowDropDown fontSize="large" />
                </h2>
                <div className="accordion-content-grow">Sample Batched Request</div>
              </div>
            </AccordionSummary>
            <AccordionDetails
              className={cx(styles.accordion_body_container, 'accordion-body', 'full-width')}
            >
              <div className={styles.accordion_body_content}>
                <LoadableMonacoEditor
                  language={editorLanguage}
                  value={batchedCode}
                  theme="dark"
                  height={600}
                  options={{
                    readOnly: true,
                    minimap: { enabled: false },
                  }}
                />
              </div>
            </AccordionDetails>
          </Accordion>
        ) : (
          <>
            {map(previewPayload, (sample, index) => {
              const excludes = get(sinkConfig, 'mapping.excludes', []);
              const code = JSONUtils.parseToFormat(
                isPlainObject(sample) ? omit(sample, excludes) : sample,
                contentType,
              );

              return (
                <Accordion
                  key={index}
                  square
                  expanded={activePanelKey === index}
                  onChange={() => {
                    selectPanel(index);
                  }}
                >
                  <AccordionSummary className="accordion-title">
                    <div className={styles.accordion_title_content}>
                      {activePanelKey === index ? (
                        <ArrowDropDown fontSize="large" />
                      ) : (
                        <ArrowRight fontSize="large" />
                      )}

                      <div className="accordion-content-grow">{`${panelHeading} ${index + 1}`}</div>
                    </div>
                  </AccordionSummary>
                  <AccordionDetails
                    className={cx(styles.accordion_body_container, 'accordion-body', 'full-width')}
                  >
                    <div className={styles.accordion_body_content}>
                      <LoadableMonacoEditor
                        language={editorLanguage}
                        value={code}
                        height={600}
                        theme="dark"
                        options={{
                          readOnly: true,
                          minimap: { enabled: false },
                        }}
                      />
                    </div>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </>
        )}
      </div>
    );
  }

  return (
    <div>
      {description ? (
        <div className={cx(styles.description)}>
          <p>{description}</p>
        </div>
      ) : null}

      {content}
    </div>
  );
};

export default SamplesPayloadTab;
